import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import { useAuth } from './AuthContext';
import UserPhotos from './UserPhotos';
import TryOnPreviews from './TryOnPreviews';
import TryOnInfo from './TryOnInfo';
import UserNotifications from './UserNotifications';

const allowedTabs = new Set(['overview', 'photos', 'tryons', 'alerts']);
const radiusOptions = [10, 25, 50, 100];
const styleGenderOptions = ['all', 'menswear', 'womenswear'];
const shoppingCountryNames = {
  IT: 'Italia',
  RO: 'Romania',
};

function profileFormFromData(profile) {
  return {
    display_name: profile?.display_name || '',
    public_nickname: profile?.preferences?.public_nickname || profile?.proposed_public_nickname || '',
    public_identity_mode: profile?.preferences?.public_identity_mode || 'nickname',
    shopping_location_label: profile?.preferences?.shopping_location_label || '',
    shopping_radius_km: profile?.preferences?.shopping_radius_km || 25,
    preferred_style_gender: profile?.preferences?.preferred_style_gender || 'all',
    preferred_style_gender_confirmed: Boolean(profile?.preferences?.style_gender_confirmed_at),
  };
}

function clearFeedCache() {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith('od_feed_cache_v2_') || key === 'od_feed_scroll_y')
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Cache invalidation is best-effort only.
  }
}

export default function Profile() {
  const { t, language } = useI18n();
  const { logout } = useAuth();
  const [tab, setTab] = useState('overview');
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(profileFormFromData(null));
  const [locationQuery, setLocationQuery] = useState('');
  const [locationResults, setLocationResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationSearching, setLocationSearching] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    setLoadingProfile(true);
    api
      .getProfile()
      .then((res) => {
        if (!mounted) return;
        setProfile(res.data);
        setForm(profileFormFromData(res.data));
        setLocationQuery(res.data?.preferences?.shopping_location_label || '');
        if (res.data?.preferences?.shopping_location_provider_id) {
          setSelectedLocation({
            provider: res.data.preferences.shopping_location_provider,
            provider_id: res.data.preferences.shopping_location_provider_id,
            label: res.data.preferences.shopping_location_label,
            latitude: res.data.preferences.shopping_location_latitude,
            longitude: res.data.preferences.shopping_location_longitude,
            country_code: res.data.preferences.shopping_location_country_code,
            admin1: res.data.preferences.shopping_location_admin1,
          });
        }
      })
      .catch((error) => {
        if (mounted) setProfileError(t(`account.error.${error.message}`));
      })
      .finally(() => {
        if (mounted) setLoadingProfile(false);
      });

    return () => {
      mounted = false;
    };
  }, [t]);

  useEffect(() => {
    const query = locationQuery.trim();
    if (query.length < 2 || selectedLocation?.label === query) {
      setLocationResults([]);
      setLocationSearching(false);
      return undefined;
    }

    let cancelled = false;
    setLocationSearching(true);
    const timeout = setTimeout(() => {
      api
        .searchLocations(query, language)
        .then((res) => {
          if (!cancelled) setLocationResults(res.data || []);
        })
        .catch(() => {
          if (!cancelled) setLocationResults([]);
        })
        .finally(() => {
          if (!cancelled) setLocationSearching(false);
        });
    }, 260);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [language, locationQuery, selectedLocation]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab');
    if (!requestedTab) return;

    if (allowedTabs.has(requestedTab)) {
      setTab(requestedTab);
    }
    navigate(location.pathname, { replace: true });
  }, [location.pathname, location.search, navigate]);

  function updateField(field, value) {
    setProfileMessage('');
    setProfileError('');
    setForm((current) => ({ ...current, [field]: value }));
  }

  function updateStyleGender(value) {
    setProfileMessage('');
    setProfileError('');
    setForm((current) => ({
      ...current,
      preferred_style_gender: value,
      preferred_style_gender_confirmed: value !== 'all',
    }));
  }

  function updateLocationQuery(value) {
    setLocationQuery(value);
    setSelectedLocation(null);
    updateField('shopping_location_label', value);
  }

  function pickLocation(location) {
    setSelectedLocation(location);
    setLocationQuery(location.label);
    setLocationResults([]);
    updateField('shopping_location_label', location.label);
  }

  const shoppingCountryCode = selectedLocation?.country_code || profile?.preferences?.shopping_location_country_code || '';
  const shoppingCountryName = shoppingCountryNames[String(shoppingCountryCode || '').toUpperCase()] || shoppingCountryCode;
  const isLocationIncomplete = !shoppingCountryCode;
  const isStyleGenderIncomplete = form.preferred_style_gender === 'all' && !form.preferred_style_gender_confirmed;

  async function saveProfile(event) {
    event.preventDefault();
    setSavingProfile(true);
    setProfileMessage('');
    setProfileError('');

    try {
      const res = await api.updateProfile({
        ...form,
        shopping_location: selectedLocation,
      });
      setProfile(res.data);
      setForm(profileFormFromData(res.data));
      clearFeedCache();
      window.dispatchEvent(new Event('od:feed-refresh'));
      window.dispatchEvent(new Event('od:notifications-refresh'));
      setProfileMessage(t('account.saved'));
    } catch (error) {
      setProfileError(t(`account.error.${error.message}`));
    } finally {
      setSavingProfile(false);
    }
  }

  async function deleteAccount() {
    if (deleteConfirm.trim().toUpperCase() !== 'DELETE') {
      setDeleteError(t('account.deleteConfirmMismatch'));
      return;
    }

    setDeletingAccount(true);
    setDeleteError('');
    try {
      await api.deleteAccount();
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      setDeleteError(t(`account.error.${error.message}`) === `account.error.${error.message}` ? t('account.deleteError') : t(`account.error.${error.message}`));
    } finally {
      setDeletingAccount(false);
    }
  }

  return (
    <div className="ct-profile-settings-page">
      <div className="ct-profile-tabs nav nav-pills" role="tablist" aria-label={t('account.title')}>
        <button
          type="button"
          className={`nav-link ${tab === 'overview' ? 'active' : ''}`}
          onClick={() => setTab('overview')}
          role="tab"
          aria-selected={tab === 'overview'}
        >
          <i className="ph ph-user"></i>
          {t('account.overview')}
        </button>
        <button
          type="button"
          className={`nav-link ${tab === 'photos' ? 'active' : ''}`}
          onClick={() => setTab('photos')}
          role="tab"
          aria-selected={tab === 'photos'}
        >
          <i className="ph ph-images-square"></i>
          {t('photos.title')}
        </button>
	        <button
	          type="button"
	          className={`nav-link ${tab === 'tryons' ? 'active' : ''}`}
          onClick={() => setTab('tryons')}
          role="tab"
          aria-selected={tab === 'tryons'}
        >
          <i className="ph ph-magic-wand"></i>
	          <TryOnInfo label={t('tryonGallery.title')} />
	        </button>
        <button
          type="button"
          className={`nav-link ${tab === 'alerts' ? 'active' : ''}`}
          onClick={() => setTab('alerts')}
          role="tab"
          aria-selected={tab === 'alerts'}
        >
          <i className="ph ph-bell-ringing"></i>
          {t('alerts.tab')}
        </button>
	      </div>

      {tab === 'overview' ? (
        <section className="ct-account-overview" aria-busy={loadingProfile}>
          <form className="ct-account-panel ct-account-form" onSubmit={saveProfile}>
            <div className={`ct-account-completion-section ${isLocationIncomplete ? 'incomplete' : ''}`}>
              {isLocationIncomplete ? (
                <span className="ct-account-incomplete-icon" aria-hidden="true">
                  <i className="ph ph-warning-circle"></i>
                </span>
              ) : null}
              <div className="ct-account-panel-head">
                <i className="ph ph-map-pin"></i>
                <div>
                  <strong>{t('account.shopAreaTitle')}</strong>
                  <p>{t('account.shopAreaCopy')}</p>
                </div>
              </div>

              <label className="ct-account-field">
                <span>{t('account.locationLabel')}</span>
                <input
                  type="text"
                  value={locationQuery}
                  onChange={(event) => updateLocationQuery(event.target.value)}
                  placeholder={t('account.locationPlaceholder')}
                  maxLength={160}
                  autoComplete="off"
                />
                {locationQuery.trim().length >= 2 ? (
                  <div className="ct-location-results" role="listbox" aria-label={t('account.locationResults')}>
                    {locationSearching ? <span>{t('account.locationSearching')}</span> : null}
                    {!locationSearching && locationResults.length === 0 && selectedLocation?.label !== locationQuery ? (
                      <span>{t('account.locationNoResults')}</span>
                    ) : null}
                    {locationResults.map((location) => (
                      <button
                        type="button"
                        role="option"
                        aria-selected="false"
                        key={`${location.provider}:${location.provider_id}`}
                        onClick={() => pickLocation(location)}
                      >
                        <strong>{location.name}</strong>
                        <em>{[location.admin1, location.country].filter(Boolean).join(', ')}</em>
                      </button>
                    ))}
                  </div>
                ) : null}
                {selectedLocation ? (
                  <small className="ct-location-selected">
                    <i className="ph ph-check-circle"></i>
                    {t('account.locationSelected')}
                  </small>
                ) : null}
                {shoppingCountryName ? (
                  <small className="ct-location-selected">
                    <i className="ph ph-shopping-bag-open"></i>
                    {t('account.shoppingCountryFromLocation', { country: shoppingCountryName })}
                  </small>
                ) : null}
              </label>

              <div className="ct-account-field">
                <span>{t('account.radiusLabel', { km: form.shopping_radius_km })}</span>
                <div className="ct-radius-options" role="group" aria-label={t('account.radiusAria')}>
                  {radiusOptions.map((radius) => (
                    <button
                      type="button"
                      key={radius}
                      className={Number(form.shopping_radius_km) === radius ? 'active' : ''}
                      onClick={() => updateField('shopping_radius_km', radius)}
                    >
                      {radius} km
                    </button>
                  ))}
                </div>
                <input
                  className="ct-radius-slider"
                  type="range"
                  min="5"
                  max="250"
                  step="5"
                  value={form.shopping_radius_km}
                  onChange={(event) => updateField('shopping_radius_km', Number(event.target.value))}
                />
              </div>

              <div className="ct-account-note">
                <i className="ph ph-lock-key"></i>
                <span>{t('account.locationPrivacy')}</span>
              </div>
            </div>

            <div className={`ct-account-completion-section ${isStyleGenderIncomplete ? 'incomplete' : ''}`}>
              {isStyleGenderIncomplete ? (
                <span className="ct-account-incomplete-icon" aria-hidden="true">
                  <i className="ph ph-warning-circle"></i>
                </span>
              ) : null}
              <div className="ct-account-panel-head">
                <i className="ph ph-users-three"></i>
                <div>
                  <strong>{t('account.styleGenderTitle')}</strong>
                  <p>{t('account.styleGenderCopy')}</p>
                </div>
              </div>

              <div className="ct-style-gender-toggle" role="radiogroup" aria-label={t('account.styleGenderTitle')}>
                {styleGenderOptions.map((option) => (
                  <label className={form.preferred_style_gender === option ? 'active' : ''} key={option}>
                    <input
                      type="radio"
                      name="preferred_style_gender"
                      value={option}
                      checked={form.preferred_style_gender === option}
                      onChange={() => updateStyleGender(option)}
                    />
                    <span>{t(`account.styleGender.${option}`)}</span>
                  </label>
                ))}
              </div>

              {form.preferred_style_gender === 'all' ? (
                <label className="ct-account-check">
                  <input
                    type="checkbox"
                    checked={form.preferred_style_gender_confirmed}
                    onChange={(event) => updateField('preferred_style_gender_confirmed', event.target.checked)}
                  />
                  <span>{t('account.styleGenderConfirm')}</span>
                </label>
              ) : null}
            </div>

            <div className="ct-account-panel-head">
              <i className="ph ph-user-circle"></i>
              <div>
                <strong>{t('account.identityTitle')}</strong>
                <p>{t('account.identityCopy')}</p>
              </div>
            </div>

            <div className="ct-identity-toggle" role="radiogroup" aria-label={t('account.identityTitle')}>
              <label className={form.public_identity_mode === 'nickname' ? 'active' : ''}>
                <input
                  type="radio"
                  name="public_identity_mode"
                  value="nickname"
                  checked={form.public_identity_mode === 'nickname'}
                  onChange={() => updateField('public_identity_mode', 'nickname')}
                />
                <span>{t('account.useNickname')}</span>
              </label>
              <label className={form.public_identity_mode === 'real_name' ? 'active' : ''}>
                <input
                  type="radio"
                  name="public_identity_mode"
                  value="real_name"
                  checked={form.public_identity_mode === 'real_name'}
                  onChange={() => updateField('public_identity_mode', 'real_name')}
                />
                <span>{t('account.useRealName')}</span>
              </label>
            </div>

            <div className="ct-account-grid">
              <label className="ct-account-field">
                <span>{t('account.realNameLabel')}</span>
                <input
                  type="text"
                  value={form.display_name}
                  onChange={(event) => updateField('display_name', event.target.value)}
                  placeholder={t('account.realNamePlaceholder')}
                  maxLength={160}
                />
              </label>
              <label className="ct-account-field">
                <span>{t('account.nicknameLabel')}</span>
                <input
                  type="text"
                  value={form.public_nickname}
                  onChange={(event) => updateField('public_nickname', event.target.value)}
                  placeholder={profile?.proposed_public_nickname || t('account.nicknamePlaceholder')}
                  maxLength={80}
                  required
                />
              </label>
            </div>

            {profile?.proposed_public_nickname ? (
              <button
                type="button"
                className="ct-nickname-suggestion"
                onClick={() => updateField('public_nickname', profile.proposed_public_nickname)}
              >
                <i className="ph ph-sparkle"></i>
                {t('account.useSuggestion', { nickname: profile.proposed_public_nickname })}
              </button>
            ) : null}

            <div className="ct-account-actions">
              <span className={profileError ? 'error' : ''}>{profileError || profileMessage}</span>
              <button type="submit" disabled={savingProfile || loadingProfile}>
                {savingProfile ? t('account.saving') : t('account.save')}
              </button>
            </div>
          </form>

          <aside className="ct-account-panel ct-account-summary">
            <div>
              <div className="ct-account-summary-head">
                <i className="ph ph-shield-check"></i>
                <strong>{t('account.privacyTitle')}</strong>
              </div>
              <p>{t('account.privacyCopy')}</p>
              <Link to="/legal" className="ct-account-legal-link">
                {t('account.legalLink')}
              </Link>
            </div>
            <div>
              <div className="ct-account-summary-head">
                <i className="ph ph-storefront"></i>
                <strong>{t('account.areaSummaryTitle')}</strong>
              </div>
              <p>
                {form.shopping_location_label
                  ? t('account.areaSummarySet', {
                      location: form.shopping_location_label,
                      km: form.shopping_radius_km,
                    })
                  : t('account.areaSummaryEmpty')}
              </p>
            </div>
            <div>
              <div className="ct-account-summary-head">
                <i className="ph ph-users-three"></i>
                <strong>{t('account.styleGenderSummaryTitle')}</strong>
              </div>
              <p>{t(`account.styleGenderSummary.${form.preferred_style_gender || 'all'}`)}</p>
            </div>
            <div>
              <div className="ct-account-summary-head">
                <i className="ph ph-magic-wand"></i>
                <strong>{t('account.publicSummaryTitle')}</strong>
              </div>
              <p>
                {t('account.publicSummaryCopy', {
                  name:
                    form.public_identity_mode === 'real_name'
                      ? form.display_name || form.public_nickname
                      : form.public_nickname,
                })}
              </p>
            </div>
            <div className="ct-account-danger-zone">
              <i className="ph ph-warning-circle"></i>
              <div>
                <strong>{t('account.deleteTitle')}</strong>
                <p>{t('account.deleteCopy')}</p>
              </div>
              <label className="ct-account-field">
                <span>{t('account.deleteConfirmLabel')}</span>
                <input
                  type="text"
                  value={deleteConfirm}
                  onChange={(event) => setDeleteConfirm(event.target.value)}
                  placeholder="DELETE"
                  autoComplete="off"
                />
              </label>
              {deleteError ? <p className="ct-account-delete-error">{deleteError}</p> : null}
              <button
                type="button"
                className="ct-account-delete-button"
                onClick={deleteAccount}
                disabled={deletingAccount}
              >
                {deletingAccount ? t('account.deleting') : t('account.deleteButton')}
              </button>
            </div>
          </aside>
        </section>
	      ) : tab === 'photos' ? (
	        <UserPhotos embedded />
	      ) : tab === 'tryons' ? (
	        <TryOnPreviews />
	      ) : (
        <UserNotifications />
	      )}
    </div>
  );
}
